import { useEffect, RefObject } from 'react';

/**
 * Hook that handles clicks outside of specified ref elements
 * @param refs Array of refs or a single ref to detect clicks outside of
 * @param handler Function to call when a click outside occurs
 */
export const useOnClickOutside = <T extends HTMLElement = HTMLElement>(
  refs: RefObject<T> | RefObject<T>[],
  handler: (event: MouseEvent | TouchEvent) => void
) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      // Convert single ref to array for unified handling
      const refArray = Array.isArray(refs) ? refs : [refs];

      // If click is inside any of the refs, don't do anything
      const clickedInside = refArray.some(ref => {
        const el = ref.current;
        // Return true if the click was inside this ref
        return el && el.contains(event.target as Node);
      });

      // If click wasn't inside any refs, call the handler
      if (!clickedInside) {
        handler(event);
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    // Clean up
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [refs, handler]); // Re-run if refs or handler changes
};
